/* ── StudyMind AI — Frontend API Layer ───────────────────────────────────────
 * Wired to the actual FastAPI backend (JWT / Bearer auth).
 *
 * Auth model: the backend uses HTTPBearer. Every protected request must send
 * `Authorization: Bearer <token>`. The token comes from /auth/signin or
 * /auth/signup and is persisted in localStorage via setAuthToken().
 * ─────────────────────────────────────────────────────────────────────────── */

const BASE = import.meta.env.VITE_API_URL || "https://studymindbackend.salonisingh.me";

/* ── Auth token helpers ───────────────────────────────────────────────────── */
let authToken = localStorage.getItem("token") || null;

export function setAuthToken(token) {
  authToken = token;
  if (token) localStorage.setItem("token", token);
  else localStorage.removeItem("token");
}

export function getAuthToken() {
  return authToken;
}

/* ── Low-level request helpers ────────────────────────────────────────────────
 * Both helpers attach the Bearer token. (reqForm previously did NOT, which
 * would have caused 401s on uploads.) */
async function req(path, opts = {}) {
  const token = getAuthToken();
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opts.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  // Some endpoints (e.g. 204) have no body
  return res.status === 204 ? null : res.json();
}

async function reqForm(path, formData, method = "POST") {
  const token = getAuthToken();
  const res = await fetch(`${BASE}${path}`, {
    method,
    body: formData,
    headers: {
      // NOTE: do NOT set Content-Type for FormData — the browser sets the
      // multipart boundary automatically.
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/* ── Auth ─────────────────────────────────────────────────────────────────────
 * Backend: POST /auth/signin {email, password}
 *          POST /auth/signup {email, password, name}
 *          GET  /auth/me
 * All return { token, user_id, email, name }. */
export async function signIn(email, password) {
  return req("/auth/signin", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function signUp(email, password, name = "") {
  return req("/auth/signup", {
    method: "POST",
    body: JSON.stringify({ email, password, name }),
  });
}

export async function getCurrentUser() {
  return req("/auth/me");
}

// No /auth/logout route exists on the backend; JWT logout is just dropping the
// token client-side.
export async function logout() {
  setAuthToken(null);
  return null;
}

// Backward-compat aliases (older code referenced login/register). They now map
// to the correct endpoints. `login` treats its first arg as an email.
export async function login(email, password) {
  return signIn(email, password);
}
export async function register(email, password, name = "") {
  return signUp(email, password, name);
}

/* ── Notebooks ─────────────────────────────────────────────────────────────────
 * Backend: GET    /notebooks
 *          GET    /notebooks/{id}
 *          POST   /notebooks            (multipart: file, title)
 *          DELETE /notebooks/{id} */
export async function getNotebooks() {
  return req("/notebooks");
}

export async function getNotebook(id) {
  return req(`/notebooks/${id}`);
}

export async function uploadNotebook(files, title, mode = "merge") {
  const list = Array.isArray(files) ? files : [files];
  const form = new FormData();
  for (const f of list) form.append("files", f);   // field name must match the backend ("files")
  form.append("title", title || (list[0]?.name ?? ""));
  form.append("mode", mode);                        // "merge" | "separate"

  const res = await reqForm("/notebooks", form);
  // Backend returns { notebooks: [...], warnings: [...] }
  return {
    notebooks: res.notebooks || (Array.isArray(res) ? res : [res]),
    warnings:  res.warnings || [],
  };
}

// Single-file helper used by UploadZone.jsx — unwraps the first notebook.
export async function uploadPDF(file, title) {
  const { notebooks } = await uploadNotebook(file, title, "merge");
  const nb = notebooks[0] || {};
  return nb.message ? nb : { ...nb, message: "Uploaded successfully" };
}

export async function deleteNotebook(id) {
  return req(`/notebooks/${id}`, { method: "DELETE" });
}

// Rename a notebook. Backend: PATCH /notebooks/{id} { title } → updated summary.
export async function renameNotebook(id, title) {
  return req(`/notebooks/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
}

/* ── Chat ───────────────────────────────────────────────────────────────────────
 * Backend: POST /notebooks/{id}/chat
 *   body { message, history, tutor_mode, socratic, stream }
 *   Streaming uses the SAME endpoint with stream:true and emits SSE lines
 *   `data: {"token": "..."}` ending with `data: [DONE]`. */
export async function chatWithNotebook(
  notebookId, message, history = [], tutorMode = "student", socraticMode = false, context = "",
) {
  return req(`/notebooks/${notebookId}/chat`, {
    method: "POST",
    body: JSON.stringify({
      message,
      history,
      tutor_mode: tutorMode,
      socratic: socraticMode,
      stream: false,
      context,
    }),
  });
}

/**
 * Streaming chat — calls onChunk(text) for each token, resolves when complete.
 * Falls back to the non-streaming call if the server doesn't return a stream.
 */
export async function chatWithNotebookStream(
  notebookId, message, history = [], tutorMode = "student", socraticMode = false, onChunk,
) {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/notebooks/${notebookId}/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      message,
      history,
      tutor_mode: tutorMode,
      socratic: socraticMode,
      stream: true,
    }),
  });

  if (!res.ok || !res.body) {
    const data = await chatWithNotebook(notebookId, message, history, tutorMode, socraticMode);
    onChunk?.(data.response || data.answer || "");
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const payload = line.slice(6).trim();
      if (payload === "[DONE]") return;
      try {
        const json = JSON.parse(payload);
        if (json.token) onChunk?.(json.token);
        else if (json.delta) onChunk?.(json.delta);
        else if (json.response) onChunk?.(json.response);
      } catch { /* ignore keep-alive / partial lines */ }
    }
  }
}

/* ── Chat history (persistent conversation) ──────────────────────────────────
 * Backend: GET    /notebooks/{id}/chat/history  → { messages: [...] }
 *          POST   /notebooks/{id}/chat/history  { messages: [...] }
 *          DELETE /notebooks/{id}/chat/history
 * Lets a notebook keep a permanent record of everything discussed, so the user
 * can come back later and pick up exactly where they left off. */
export async function getChatHistory(notebookId) {
  const res = await req(`/notebooks/${notebookId}/chat/history`);
  return res?.messages || [];
}

export async function saveChatMessages(notebookId, messages) {
  if (!messages?.length) return null;
  // Only persist the fields the backend stores; drop transient UI flags.
  const clean = messages.map(m => ({
    role: m.role,
    content: m.content || "",
    citations: m.citations || [],
    mode: m.mode || "",
  }));
  return req(`/notebooks/${notebookId}/chat/history`, {
    method: "POST",
    body: JSON.stringify({ messages: clean }),
  });
}

export async function clearChatHistory(notebookId) {
  return req(`/notebooks/${notebookId}/chat/history`, { method: "DELETE" });
}

/* ── Artifacts ──────────────────────────────────────────────────────────────────
 * Backend: POST /notebooks/{id}/artifacts
 *   body { artifact_type, count, focus, tutor_mode }
 * Valid artifact_type: briefing, study_guide, flashcards, quiz, timeline,
 *                      formulas, mind_map */
export async function generateArtifact(
  notebookId, artifactType, numQuestions = 6, customPrompt = "", tutorMode = "student", signal,
) {
  return req(`/notebooks/${notebookId}/artifacts`, {
    method: "POST",
    signal,
    body: JSON.stringify({
      artifact_type: artifactType,
      count: numQuestions,
      focus: customPrompt,
      tutor_mode: tutorMode,
    }),
  });
}

/* ── Agent / Study plan ─────────────────────────────────────────────────────────
 * Backend: POST /notebooks/{id}/agent       { goal, exam_date, tutor_mode }
 *          POST /notebooks/{id}/study-plan  { exam_date, daily_hours, focus_topics } */
export async function generateAgentWorkflow(
  notebookId, task, _customPrompt = "", tutorMode = "student", examDate = "", signal,
) {
  return req(`/notebooks/${notebookId}/agent`, {
    method: "POST",
    signal,
    body: JSON.stringify({
      goal: task,
      exam_date: examDate,
      tutor_mode: tutorMode,
    }),
  });
}

export async function generateStudyPlan(
  notebookId, examDate = "", hoursPerDay = 2.0, weakAreas = [], signal,
) {
  return req(`/notebooks/${notebookId}/study-plan`, {
    method: "POST",
    signal,
    body: JSON.stringify({
      exam_date: examDate,
      daily_hours: hoursPerDay,
      focus_topics: weakAreas,
    }),
  });
}

/* ── Cross-notebook search ────────────────────────────────────────────────────
 * Backend: POST /search { query, notebook_ids } */
export async function globalSearch(query, notebookIds = []) {
  return req("/search", {
    method: "POST",
    body: JSON.stringify({ query, notebook_ids: notebookIds }),
  });
}

/* ── Exam ───────────────────────────────────────────────────────────────────────
 * Backend (examRoutes.py):
 *   POST /notebooks/{id}/exam/generate { exam_type, total_marks }
 *   POST /notebooks/{id}/exam/review   { exam_paper, answers, exam_type }
 *   GET  /notebooks/{id}/exam/history */
export async function generateExamPaper(notebookId, examType, totalMarks, signal) {
  return req(`/notebooks/${notebookId}/exam/generate`, {
    method: "POST",
    signal,
    body: JSON.stringify({ exam_type: examType, total_marks: totalMarks }),
  });
}

export async function reviewExam(notebookId, examPaper, answers, signal) {
  return req(`/notebooks/${notebookId}/exam/review`, {
    method: "POST",
    signal,
    body: JSON.stringify({
      exam_paper: examPaper,
      answers,
      exam_type: examPaper?.exam_type || "full",
    }),
  });
}

export async function getExamHistory(notebookId) {
  return req(`/notebooks/${notebookId}/exam/history`);
}

/* ── Progress & quiz ──────────────────────────────────────────────────────────
 * Backend: GET  /progress
 *          GET  /notebooks/{id}/weak-areas
 *          POST /notebooks/{id}/quiz-result */
export async function getProgressStats() {
  return req("/progress");
}

export async function getWeakAreas(notebookId) {
  return req(`/notebooks/${notebookId}/weak-areas`);
}

export async function submitQuizResult(notebookId, topic, score, total, weakTopics = []) {
  return req(`/notebooks/${notebookId}/quiz-result`, {
    method: "POST",
    body: JSON.stringify({ topic, score, total, weak_topics: weakTopics }),
  });
}

/* ── Syllabus ─────────────────────────────────────────────────────────────────
 * Backend: POST /syllabus/generate  (multipart: files) */
export async function generateSyllabusNotes(files, signal) {
  const list = Array.isArray(files) ? files : [files];
  const form = new FormData();
  for (const f of list) form.append("files", f);

  const token = getAuthToken();
  const res = await fetch(`${BASE}/syllabus/generate`, {
    method: "POST",
    body: form,
    signal,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/* ── YouTube ──────────────────────────────────────────────────────────────────
 * Add these functions to the BOTTOM of frontend/src/api.js.
 *
 * Backend: GET    /youtube/session
 *          POST   /youtube/videos                { urls: [...] }
 *          GET    /youtube/videos/{video_id}
 *          DELETE /youtube/videos/{video_id}
 *          POST   /youtube/videos/{video_id}/chat  { message, history }
 *          POST   /youtube/videos/{video_id}/regenerate
 *          GET    /youtube/session/export
 * ─────────────────────────────────────────────────────────────────────────── */

/** Get (or create) the user's YouTube session with all processed videos. */
export async function getYoutubeSession() {
  return req("/youtube/session");
}

/**
 * Submit one or more YouTube URLs for processing.
 * Returns { accepted: [video_ids], rejected: [bad_urls], message }
 * Processing is async — poll getYoutubeVideo() until status === "ready".
 */
export async function addYoutubeVideos(urls) {
  return req("/youtube/videos", {
    method: "POST",
    body: JSON.stringify({ urls }),
  });
}

/**
 * Poll for a single video's current state.
 * Returns { video_id, status, title, notes, has_transcript, ... }
 * status is "processing" or "ready".
 */
export async function getYoutubeVideo(videoId) {
  return req(`/youtube/videos/${videoId}`);
}

/** Remove a video from the user's session. */
export async function deleteYoutubeVideo(videoId) {
  return req(`/youtube/videos/${videoId}`, { method: "DELETE" });
}

/**
 * Ask a question about a specific video.
 * Returns { response, video_id, has_transcript }
 */
export async function chatWithYoutubeVideo(videoId, message, history = []) {
  return req(`/youtube/videos/${videoId}/chat`, {
    method: "POST",
    body: JSON.stringify({ message, history }),
  });
}

/** Force-regenerate notes for a video. */
export async function regenerateYoutubeNotes(videoId) {
  return req(`/youtube/videos/${videoId}/regenerate`, { method: "POST" });
}

/** Get all notes from the current session as combined markdown (for PDF export). */
export async function exportYoutubeSession() {
  return req("/youtube/session/export");
}
